import { useCallback, useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { Guid, UnprintedChecks } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";

export type UnprintedChecksStatus = "loading" | "ready" | "error";

export interface UseUnprintedChecksResult {
  /** `null` until the first read for THIS account resolves, and `null` if it failed - never zeros for "unknown". */
  unprinted: UnprintedChecks | null;
  status: UnprintedChecksStatus;
  /** True while a read is in flight (the first one included). The last answer stays on screen meanwhile. */
  fetching: boolean;
  error: string | null;
  /** Re-reads the waiting count. Resolves once the read has settled. */
  refetch: () => void;
}

/**
 * ef-062-7 - how many flagged payments wait for a check on a register account, from
 * GET /api/demo/books/register/unprinted-checks. The count and total are the server's: nothing
 * here sums. Reads on mount, on an account change, and whenever `refetch` is called.
 */
export function useUnprintedChecks(registerAccountId: Guid): UseUnprintedChecksResult {
  const [loaded, setLoaded] = useState<{ accountId: Guid; value: UnprintedChecks } | null>(null);
  const [failure, setFailure] = useState<{ accountId: Guid; message: string } | null>(null);
  const [fetching, setFetching] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setFetching(true);
    demoApi
      .getUnprintedChecks(registerAccountId, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        setLoaded({ accountId: registerAccountId, value });
        setFailure(null);
        setFetching(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setFailure({ accountId: registerAccountId, message: describeReadFailure(err) });
        setFetching(false);
      });
    return () => controller.abort();
  }, [registerAccountId, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  // Another account's answer is not this account's: derive, do not carry it across.
  const unprinted = loaded?.accountId === registerAccountId ? loaded.value : null;
  const failed = failure?.accountId === registerAccountId ? failure : null;
  const status: UnprintedChecksStatus = failed ? "error" : unprinted ? "ready" : "loading";
  return { unprinted, status, fetching, error: failed?.message ?? null, refetch };
}
